import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { hydraUiApi } from '@/services/api';
import type { AugmentedAccount, AugmentedUser, Workspace } from '@/services/auth/types';
import type { HookMutationConfig } from '../shared/react-query-types';
import { parseUserSettingsResponse, userSettingsKeys, type SideNavPreferences, type UserSettings } from './entity';

export interface UpdateUserSettingsInput {
  accountId: number;
  userId: number;
  sideNavPreferences: SideNavPreferences;
}

export async function updateUserSettings(input: UpdateUserSettingsInput): Promise<UserSettings> {
  const response = await hydraUiApi.users.updateSettings({
    params: {
      path: {
        account_id: String(input.accountId),
        id: String(input.userId),
      },
    },
    body: {
      side_nav_preferences: input.sideNavPreferences,
    },
  });

  return parseUserSettingsResponse(response, {
    accountId: input.accountId,
    userId: input.userId,
  });
}

type UseUpdateUserSettingsVariables = Omit<UpdateUserSettingsInput, 'accountId' | 'userId'>;

type AuthenticatedQueryData = {
  account: AugmentedAccount | null;
  user: AugmentedUser | null;
  workspaces: Workspace[] | null;
};

type UpdateUserSettingsMutationContext = {
  previousAuthenticatedQueryData: AuthenticatedQueryData | undefined;
  previousUserSettings: UserSettings | undefined;
  mutationContext: unknown;
};

type CachedUserSettingsSnapshot = Omit<UpdateUserSettingsMutationContext, 'mutationContext'>;

function setCachedUserSettings(
  qc: QueryClient,
  settings: UserSettings,
  fallbackSideNavPreferences?: SideNavPreferences,
) {
  const sideNavPreferences = settings.side_nav_preferences ?? fallbackSideNavPreferences;

  qc.setQueryData(userSettingsKeys.detail(settings.accountId, settings.userId), settings);

  if (sideNavPreferences != null) {
    setAuthenticatedSideNavPreferences(qc, sideNavPreferences);
  }
}

function setAuthenticatedSideNavPreferences(qc: QueryClient, sideNavPreferences: SideNavPreferences) {
  const auth = qc.getQueryData<AuthenticatedQueryData>(['authenticated']);

  if (auth?.user == null) {
    return;
  }

  qc.setQueryData<AuthenticatedQueryData>(['authenticated'], {
    ...auth,
    user: {
      ...auth.user,
      settings: {
        ...auth.user.settings,
        side_nav_preferences: sideNavPreferences,
      },
    },
  });
}

function restoreCachedUserSettings(
  qc: QueryClient,
  accountId: number | null | undefined,
  userId: number | null | undefined,
  previousUserSettings: UserSettings | undefined,
  previousAuthenticatedQueryData: AuthenticatedQueryData | undefined,
) {
  if (accountId != null && userId != null) {
    if (previousUserSettings == null) {
      qc.removeQueries({ exact: true, queryKey: userSettingsKeys.detail(accountId, userId) });
    } else {
      qc.setQueryData(userSettingsKeys.detail(accountId, userId), previousUserSettings);
    }
  }

  if (previousAuthenticatedQueryData == null) {
    qc.removeQueries({ exact: true, queryKey: ['authenticated'] });
  } else {
    qc.setQueryData<AuthenticatedQueryData>(['authenticated'], previousAuthenticatedQueryData);
  }
}

function getSideNavPreferencesFromSnapshot(snapshot: CachedUserSettingsSnapshot | undefined) {
  return (
    snapshot?.previousUserSettings?.side_nav_preferences ??
    snapshot?.previousAuthenticatedQueryData?.user?.settings?.side_nav_preferences
  );
}

function getConfirmedSnapshot(
  qc: QueryClient,
  settings: UserSettings,
  fallbackSideNavPreferences?: SideNavPreferences,
): CachedUserSettingsSnapshot {
  const sideNavPreferences = settings.side_nav_preferences ?? fallbackSideNavPreferences;
  const authenticatedQueryData = qc.getQueryData<AuthenticatedQueryData>(['authenticated']);

  return {
    previousUserSettings: settings,
    previousAuthenticatedQueryData:
      authenticatedQueryData?.user == null || sideNavPreferences == null
        ? authenticatedQueryData
        : {
            ...authenticatedQueryData,
            user: {
              ...authenticatedQueryData.user,
              settings: {
                ...authenticatedQueryData.user.settings,
                side_nav_preferences: sideNavPreferences,
              },
            },
          },
  };
}

interface UseUpdateUserSettingsOptions {
  accountId: number | null | undefined;
  userId: number | null | undefined;
  mutationConfig?: HookMutationConfig<UserSettings, UseUpdateUserSettingsVariables>;
}

export const useUpdateUserSettings = ({ accountId, userId, mutationConfig }: UseUpdateUserSettingsOptions) => {
  const qc = useQueryClient();
  const latestSideNavPreferencesRef = useRef<SideNavPreferences | undefined>(undefined);
  const confirmedSnapshotRef = useRef<CachedUserSettingsSnapshot | undefined>(undefined);
  const pendingMutationCountRef = useRef(0);
  const { onError, onMutate, onSettled, onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    ...rest,
    scope: accountId != null && userId != null ? { id: `user-settings:${accountId}:${userId}` } : undefined,
    mutationFn: (variables: UseUpdateUserSettingsVariables) => {
      if (accountId == null || userId == null) {
        throw new Error('Cannot update user settings without an authenticated user');
      }

      return updateUserSettings({ accountId, userId, ...variables });
    },
    onMutate: async (variables, context): Promise<UpdateUserSettingsMutationContext> => {
      const previousUserSettings =
        accountId != null && userId != null
          ? qc.getQueryData<UserSettings>(userSettingsKeys.detail(accountId, userId))
          : undefined;
      const previousAuthenticatedQueryData = qc.getQueryData<AuthenticatedQueryData>(['authenticated']);

      latestSideNavPreferencesRef.current = variables.sideNavPreferences;
      pendingMutationCountRef.current += 1;
      confirmedSnapshotRef.current ??= {
        previousAuthenticatedQueryData,
        previousUserSettings,
      };

      if (accountId != null && userId != null) {
        setCachedUserSettings(qc, {
          accountId,
          userId,
          side_nav_preferences: variables.sideNavPreferences,
        });
      }

      return {
        previousAuthenticatedQueryData,
        previousUserSettings,
        mutationContext: await onMutate?.(variables, context),
      };
    },
    onError: (error, variables, context, mutation) => {
      if (latestSideNavPreferencesRef.current === variables.sideNavPreferences) {
        const snapshot = confirmedSnapshotRef.current ?? context;
        latestSideNavPreferencesRef.current = getSideNavPreferencesFromSnapshot(snapshot);
        restoreCachedUserSettings(
          qc,
          accountId,
          userId,
          snapshot?.previousUserSettings,
          snapshot?.previousAuthenticatedQueryData,
        );
        onError?.(error, variables, context?.mutationContext, mutation);
      }
    },
    onSuccess: (settings, variables, context, mutation) => {
      const nextSettings = {
        ...settings,
        side_nav_preferences: settings.side_nav_preferences ?? variables.sideNavPreferences,
      };
      confirmedSnapshotRef.current = getConfirmedSnapshot(qc, nextSettings, variables.sideNavPreferences);

      if (latestSideNavPreferencesRef.current === variables.sideNavPreferences) {
        latestSideNavPreferencesRef.current = nextSettings.side_nav_preferences;
        setCachedUserSettings(qc, nextSettings, variables.sideNavPreferences);
      }

      onSuccess?.(nextSettings, variables, context?.mutationContext, mutation);
    },
    onSettled: (settings, error, variables, context, mutation) => {
      pendingMutationCountRef.current = Math.max(0, pendingMutationCountRef.current - 1);

      if (pendingMutationCountRef.current === 0) {
        confirmedSnapshotRef.current = undefined;
      }

      onSettled?.(settings, error, variables, context?.mutationContext, mutation);
    },
  });
};
